import type { SQLiteDatabase } from 'expo-sqlite';

import { getSubtypeMeta, getSystemMeta } from '@/data/systems';

export type TimelineKind =
  | 'completion'
  /** A "did not do it" close — the task was skipped rather than completed. */
  | 'skip'
  | 'expense'
  | 'system'
  | 'inventory'
  | 'document'
  | 'contact'
  | 'note'
  | 'quote';

export interface TimelineEntry {
  id: string;
  kind: TimelineKind;
  at: number;
  title: string;
  subtitle: string | null;
  amount_cents: number | null;
  ref_id: string;
}

interface Row {
  id: string;
  kind: TimelineKind;
  at: number;
  title: string;
  subtitle: string | null;
  amount_cents: number | null;
  ref_id: string;
}

export async function loadTimeline(
  db: SQLiteDatabase,
  propertyId: string,
  limit = 200
): Promise<TimelineEntry[]> {
  const rows = await db.getAllAsync<Row>(
    `
    SELECT
      'tc:' || tc.id AS id,
      -- Split done vs skip into separate timeline kinds so the filters
      -- and visual treatment can diverge. Existing rows default to
      -- 'done' from the migration, so backfill is automatic.
      CASE WHEN tc.kind = 'skip' THEN 'skip' ELSE 'completion' END AS kind,
      tc.completed_at AS at,
      t.title AS title,
      COALESCE(tc.vendor, tc.notes) AS subtitle,
      tc.cost_cents AS amount_cents,
      t.id AS ref_id
    FROM task_completion tc
    JOIN task t ON t.id = tc.task_id
    WHERE t.property_id = ? AND t.deleted_at IS NULL

    UNION ALL

    SELECT
      'ex:' || e.id AS id,
      'expense' AS kind,
      e.paid_on AS at,
      COALESCE(e.description, e.category) AS title,
      e.vendor AS subtitle,
      e.amount_cents AS amount_cents,
      e.id AS ref_id
    FROM expense e
    WHERE e.property_id = ? AND e.deleted_at IS NULL

    UNION ALL

    SELECT
      'sy:' || s.id AS id,
      'system' AS kind,
      s.created_at AS at,
      s.nickname AS title,
      s.kind || (CASE WHEN s.subtype IS NOT NULL THEN '|' || s.subtype ELSE '' END) AS subtitle,
      NULL AS amount_cents,
      s.id AS ref_id
    FROM system s
    WHERE s.property_id = ? AND s.deleted_at IS NULL

    UNION ALL

    SELECT
      'in:' || i.id AS id,
      'inventory' AS kind,
      i.created_at AS at,
      i.name AS title,
      COALESCE(i.brand, i.room) AS subtitle,
      i.purchase_cents AS amount_cents,
      i.id AS ref_id
    FROM inventory_item i
    WHERE i.property_id = ? AND i.deleted_at IS NULL

    UNION ALL

    SELECT
      'do:' || d.id AS id,
      'document' AS kind,
      d.created_at AS at,
      d.title AS title,
      d.kind AS subtitle,
      d.cost_cents AS amount_cents,
      d.id AS ref_id
    FROM document d
    WHERE d.property_id = ? AND d.deleted_at IS NULL

    UNION ALL

    SELECT
      'co:' || c.id AS id,
      'contact' AS kind,
      c.created_at AS at,
      c.name AS title,
      COALESCE(c.role, c.company) AS subtitle,
      NULL AS amount_cents,
      c.id AS ref_id
    FROM contact c
    WHERE c.property_id = ? AND c.deleted_at IS NULL

    UNION ALL

    -- Quotes — estimates received are home events too. Subtitle carries
    -- project + vendor so the row reads "Roof replacement · ABC Roofing".
    SELECT
      'qt:' || q.id AS id,
      'quote' AS kind,
      q.quoted_on AS at,
      COALESCE(NULLIF(TRIM(q.vendor), ''), q.category) AS title,
      COALESCE(q.project, q.description) AS subtitle,
      q.amount_cents AS amount_cents,
      q.id AS ref_id
    FROM quote q
    WHERE q.property_id = ? AND q.deleted_at IS NULL

    UNION ALL

    -- Notes — surface alongside completions so the timeline is a true
    -- "what happened to this home" record. We use updated_at so editing
    -- an old note bumps it forward (matches how notes feel — the freshest
    -- thought wins). Subtitle gets a short snippet so the row reads
    -- without opening the editor.
    SELECT
      'nt:' || n.id AS id,
      'note' AS kind,
      n.updated_at AS at,
      COALESCE(
        NULLIF(TRIM(n.title), ''),
        SUBSTR(
          TRIM(REPLACE(REPLACE(REPLACE(n.body, '#', ''), '*', ''), '_', '')),
          1, 80
        )
      ) AS title,
      SUBSTR(REPLACE(n.body, CHAR(10), ' '), 1, 140) AS subtitle,
      NULL AS amount_cents,
      n.id AS ref_id
    FROM note n
    WHERE n.property_id = ? AND n.deleted_at IS NULL

    ORDER BY at DESC
    LIMIT ?
    `,
    propertyId,
    propertyId,
    propertyId,
    propertyId,
    propertyId,
    propertyId,
    propertyId,
    propertyId,
    limit
  );

  return rows.map((r) => {
    if (r.kind === 'system') {
      const [rawKind, rawSub] = (r.subtitle ?? '').split('|');
      const meta = getSystemMeta(rawKind ?? '');
      const subtype = rawSub ? getSubtypeMeta(rawKind, rawSub) : null;
      return {
        ...r,
        title: r.title?.trim() || meta?.label || rawKind || 'System added',
        subtitle: subtype?.label ?? meta?.label ?? null,
      };
    }
    if (r.kind === 'note') {
      // Note bodies stored as HTML/markdown — strip chrome so the
      // activity row's subtitle reads as plain text, not raw markup.
      // Title can carry markup too (when there's no explicit title
      // and the SQL uses a SUBSTR of the body) — clean both.
      return {
        ...r,
        title: stripForDisplay(r.title) || 'Note',
        subtitle: stripForDisplay(r.subtitle),
      };
    }
    return r;
  });
}

/**
 * Strip HTML tags + markdown chrome from a string so it renders as
 * plain text in compact contexts (timeline row, activity card, etc.).
 * Collapses whitespace and trims to a single line.
 */
function stripForDisplay(s: string | null): string | null {
  if (!s) return s;
  return s
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/[#*_`>]/g, '')
    .replace(/\s+/g, ' ')
    .trim() || null;
}

export function formatTimelineDate(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function groupByMonth(entries: TimelineEntry[]): { label: string; items: TimelineEntry[] }[] {
  const groups: { label: string; items: TimelineEntry[] }[] = [];
  let current: { label: string; items: TimelineEntry[] } | null = null;
  for (const e of entries) {
    const d = new Date(e.at);
    const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    if (!current || current.label !== label) {
      current = { label, items: [] };
      groups.push(current);
    }
    current.items.push(e);
  }
  return groups;
}
